import React from 'react';
import { PAGE_SIZES } from '../utils/constants';
import SafeIcon from '../common/SafeIcon';
import {
  FiSettings,
  FiLayout,
  FiGrid,
  FiDroplet,
} from 'react-icons/fi';

const SettingsPanel = ({ settings, setSettings }) => {
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setSettings((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const presetColors = [
    '#ffffff',
    '#000000',
    '#f3f4f6',
    '#e5e7eb',
    '#d1d5db',
  ];

  const imageCounts = [1, 2, 4, 6, 8, 12];

  return (
    <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gray-50 px-8 py-5 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiSettings} className="text-blue-600 text-xl" />
          <h2 className="text-base font-black text-gray-800 uppercase tracking-wider">
            Document Settings
          </h2>
        </div>

        <div className="flex items-center gap-6">
          <Toggle
            label="Page Numbers"
            name="showPageNumbers"
            checked={settings.showPageNumbers}
            onChange={handleChange}
          />
          <Toggle
            label="Image Details"
            name="showDetails"
            checked={settings.showDetails}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="space-y-6">
          <SectionHeading icon={FiLayout} label="Layout & Size" />

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Page Size
            </label>
            <select
              name="pageSize"
              value={settings.pageSize}
              onChange={handleChange}
              className="w-full p-3.5 bg-gray-50 border border-gray-300 rounded-xl text-base font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            >
              {Object.keys(PAGE_SIZES).map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
              <option value="Custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Orientation
            </label>
            <div className="grid grid-cols-3 gap-3">
              {['auto', 'portrait', 'landscape'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() =>
                    setSettings((prev) => ({
                      ...prev,
                      orientation: mode,
                    }))
                  }
                  className={`py-3 text-sm font-black rounded-xl capitalize border transition-all ${
                    settings.orientation === mode
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
              <SafeIcon icon={FiDroplet} className="text-gray-400" />
              Image Background
            </label>

            <div className="flex items-center gap-3">
              <div className="flex gap-1.5 p-1.5 bg-gray-50 border border-gray-200 rounded-xl">
                {presetColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        imageBackground: color,
                      }))
                    }
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      settings.imageBackground === color
                        ? 'border-blue-500 scale-110 shadow-sm'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>

              <input
                type="color"
                name="imageBackground"
                value={settings.imageBackground}
                onChange={handleChange}
                className="w-10 h-10 rounded-xl cursor-pointer border-none bg-transparent"
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <SectionHeading icon={FiGrid} label="Grid & Scaling" />

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Images Per Page
            </label>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {imageCounts.map((count) => (
                <button
                  key={count}
                  type="button"
                  onClick={() =>
                    setSettings((prev) => ({
                      ...prev,
                      imagesPerPage: count,
                    }))
                  }
                  className={`py-3 text-sm font-black rounded-xl border transition-all ${
                    Number(settings.imagesPerPage) === count
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Scaling Mode
            </label>
            <select
              name="fitMode"
              value={settings.fitMode}
              onChange={handleChange}
              className="w-full p-3.5 bg-gray-50 border border-gray-300 rounded-xl text-base font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            >
              <option value="fit">Fit (Preserve aspect ratio)</option>
              <option value="fill">Fill (Crop to fill page)</option>
            </select>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-bold text-gray-700">Margins</label>
              <span className="text-sm font-black text-blue-700 bg-blue-100 px-3 py-1 rounded-lg border border-blue-200">
                {settings.margin} mm
              </span>
            </div>

            <input
              type="range"
              name="margin"
              min="0"
              max="50"
              value={settings.margin}
              onChange={handleChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
        </div>
      </div>

      <div className="p-8 bg-gray-50 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3">
            Blank Pages
          </label>
          <select
            name="blankPages"
            value={settings.blankPages}
            onChange={handleChange}
            className="w-full p-3.5 bg-white border border-gray-300 rounded-xl text-base font-medium focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
          >
            <option value="none">None</option>
            <option value="after">Add blank page after each</option>
            <option value="before">Add blank page before each</option>
            <option value="both">Add blank pages both sides</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3">
            Save As
          </label>
          <input
            type="text"
            name="filename"
            value={settings.filename}
            onChange={handleChange}
            className="w-full p-3.5 bg-white border border-gray-300 rounded-xl text-base font-medium focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
            placeholder="filename.pdf"
          />
        </div>
      </div>
    </div>
  );
};

const Toggle = ({ label, name, checked, onChange }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs font-black text-gray-500 uppercase">{label}</span>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="sr-only peer"
      />
      <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
    </label>
  </div>
);

const SectionHeading = ({ icon, label }) => (
  <div className="flex items-center gap-2 mb-2">
    <SafeIcon icon={icon} className="text-gray-400 text-sm" />
    <span className="text-xs font-black text-gray-400 uppercase tracking-widest">
      {label}
    </span>
  </div>
);

export default SettingsPanel;